import { useQuery } from '@tanstack/react-query';
import { Copy, MailPlus } from 'lucide-react';
import { useState } from 'react';
import { api, useAction } from '../../api/hooks';
import { useIsAdmin } from '../../api/session';
import type { Invitation, ProjectSummary } from '../../api/types';
import { useCrumbs } from '../../layout/crumbs';
import { StateBadge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { EmptyState, ErrorAlert, PageUnavailable, QueryView } from '../../ui/feedback';
import { Checkbox, Field, Input, Select } from '../../ui/forms';
import { ConfirmDialog, Modal } from '../../ui/Modal';
import { ActorName, PageHeader, Tabs, usePageTitle } from '../../ui/misc';
import { Timestamp } from '../../ui/Timestamp';

/** IDN-02: revocable invitations with role and initial memberships; shareable link (no email in v1). */
export function InvitationsPage() {
  usePageTitle('Invitations');
  useCrumbs([{ label: 'Administration' }, { label: 'Invitations' }]);
  const admin = useIsAdmin();
  const q = useQuery({
    queryKey: ['invitations'],
    queryFn: () => api.get<Invitation[]>('/admin/invitations'),
    enabled: admin,
  });
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState<Invitation | null>(null);
  const [tab, setTab] = useState<'pending' | 'all'>('pending');
  const revoke = useAction((i: Invitation) => api.post(`/admin/invitations/${i.id}/revoke`), {
    success: 'Invitation revoked',
    invalidate: [['invitations']],
    onSuccess: () => setRevoking(null),
  });
  if (!admin) return <PageUnavailable />;
  return (
    <div className="page">
      <PageHeader
        title="Invitations"
        subtitle="Share the admission link with the invitee. It is shown once and expires."
        actions={<Button onClick={() => setCreating(true)}>Create invitation</Button>}
      />
      <QueryView query={q}>
        {(list) => {
          const shown = tab === 'pending' ? list.filter((i) => i.state === 'pending') : list;
          return (
            <>
              <Tabs
                label="Invitation state"
                value={tab}
                onChange={setTab}
                tabs={[
                  {
                    value: 'pending',
                    label: 'Pending',
                    count: list.filter((i) => i.state === 'pending').length,
                  },
                  { value: 'all', label: 'All', count: list.length },
                ]}
              />
              {shown.length === 0 ? (
                <EmptyState icon={MailPlus} title="No invitations">
                  Create an invitation to admit someone.
                </EmptyState>
              ) : (
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Projects</th>
                        <th>State</th>
                        <th>Expires</th>
                        <th>Invited by</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {shown.map((i) => (
                        <tr key={i.id}>
                          <td className="mono small">{i.displayId}</td>
                          <td className="ink">{i.email}</td>
                          <td>{i.role === 'admin' ? 'Admin' : 'Member'}</td>
                          <td className="small">
                            {i.projects.map((p) => p.key).join(', ') || <span className="muted">None</span>}
                          </td>
                          <td>
                            <StateBadge state={i.state} />
                          </td>
                          <td className="small">
                            <Timestamp value={i.expiresAt} />
                          </td>
                          <td className="small">
                            <ActorName actor={i.createdBy} />
                          </td>
                          <td>
                            {i.state === 'pending' && (
                              <Button variant="ghost" size="sm" onClick={() => setRevoking(i)}>
                                Revoke
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          );
        }}
      </QueryView>
      {creating && <CreateInvitation onClose={() => setCreating(false)} />}
      {revoking && (
        <ConfirmDialog
          title={`Revoke invitation for ${revoking.email}?`}
          confirmLabel="Yes, revoke"
          variant="danger"
          onClose={() => setRevoking(null)}
          onConfirm={() => revoke.mutate(revoking)}
          pending={revoke.isPending}
          error={revoke.error}
        >
          <p>The link stops working immediately.</p>
        </ConfirmDialog>
      )}
    </div>
  );
}

function CreateInvitation({ onClose }: { onClose: () => void }) {
  const projects = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.get<ProjectSummary[]>('/projects'),
  });
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'user' | 'admin'>('user');
  const [projectIds, setProjectIds] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);
  const create = useAction(
    () => api.post<{ link: string; expiresAt: string }>('/admin/invitations', { email, role, projectIds }),
    { invalidate: [['invitations']] },
  );
  if (create.data) {
    return (
      <Modal
        title="Invitation created"
        onClose={onClose}
        actions={<Button onClick={onClose}>I've copied the link — close</Button>}
        dismissible={false}
      >
        <div className="secret-panel" role="alert">
          <span className="strong">Copy now — this link will not be shown again.</span>
          <div className="code">{create.data.link}</div>
          <div className="row">
            <Button
              size="sm"
              variant="secondary"
              icon={<Copy size={14} />}
              onClick={() => navigator.clipboard.writeText(create.data!.link).then(() => setCopied(true))}
            >
              {copied ? 'Copied' : 'Copy link'}
            </Button>
            <span className="small">
              Expires <Timestamp value={create.data.expiresAt} />
            </span>
          </div>
        </div>
        <p className="small muted">
          Send it to {email} through your usual channel. They must sign in with an identity whose verified
          email matches.
        </p>
      </Modal>
    );
  }
  return (
    <Modal
      title="Create invitation"
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => create.mutate(undefined)} loading={create.isPending} disabled={!email}>
            Create invitation
          </Button>
        </>
      }
    >
      <Field label="Email">
        {(p) => (
          <Input
            {...p}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            data-autofocus
          />
        )}
      </Field>
      <Field label="Role">
        {(p) => (
          <Select {...p} value={role} onChange={(e) => setRole(e.target.value as 'user' | 'admin')}>
            <option value="user">Member</option>
            <option value="admin">Admin</option>
          </Select>
        )}
      </Field>
      <div className="field">
        <span className="label">Initial projects</span>
        <div className="stack sm" style={{ maxHeight: 200, overflowY: 'auto' }}>
          {(projects.data ?? [])
            .filter((p) => !p.archived)
            .map((p) => (
              <Checkbox
                key={p.id}
                label={
                  <>
                    <span className="mono muted">{p.key}</span> {p.name}
                  </>
                }
                checked={projectIds.includes(p.id)}
                onChange={(on) =>
                  setProjectIds(on ? [...projectIds, p.id] : projectIds.filter((x) => x !== p.id))
                }
              />
            ))}
        </div>
      </div>
      <ErrorAlert error={create.error} />
    </Modal>
  );
}
